/**
 * Public Legal pages — Privacy Policy & Terms and Conditions.
 * Content is CMS-driven (see backend/routers/cms.py `legal.*` fields).
 * Theme is pulled from the same CMS `customer_theme` block used by the landing
 * page + customer login, so branding stays consistent across the public surface.
 */
import React from "react";
import { Link } from "react-router-dom";
import DOMPurify from "dompurify";
import { useLandingCMS } from "@/hooks/useLandingCMS";
import { ArrowLeft, Fuel, ShieldCheck, ScrollText, Mail } from "lucide-react";

// Strict allow-list: only bold + anchors used by our mini markdown renderer.
const SANITIZE_CONFIG = {
  ALLOWED_TAGS: ["strong", "a"],
  ALLOWED_ATTR: ["href", "class", "target", "rel"],
  ALLOW_DATA_ATTR: false,
};
const safeInline = (html) => DOMPurify.sanitize(html, SANITIZE_CONFIG);

// Minimal markdown renderer — handles: # H1, ## H2, ### H3, **bold**, [link](url), bullet lists, paragraphs.
function renderMarkdown(md, accent) {
  if (!md) return null;
  const lines = md.split("\n");
  const nodes = [];
  let bulletBuffer = [];
  const flushBullets = () => {
    if (bulletBuffer.length) {
      const bulletsToRender = bulletBuffer;
      nodes.push(
        <ul key={`ul-${nodes.length}`} className="mb-5 ml-1 space-y-2 text-slate-700">
          {bulletsToRender.map((b, i) => (
            <li key={`${nodes.length}-${b}-${i}`} className="flex items-start gap-2.5 leading-relaxed">
              <span className="mt-2 inline-block h-1.5 w-1.5 flex-shrink-0 rounded-full" style={{ backgroundColor: accent }} />
              <span dangerouslySetInnerHTML={{ __html: safeInline(inline(b)) }} />
            </li>
          ))}
        </ul>,
      );
      bulletBuffer = [];
    }
  };
  const inline = (t) =>
    t
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, `<a href="$2" class="underline" style="color:${accent}">$1</a>`);

  lines.forEach((raw, idx) => {
    const line = raw.trim();
    if (!line) { flushBullets(); return; }
    if (line.startsWith("### ")) {
      flushBullets();
      nodes.push(
        <h3 key={idx} className="mb-3 mt-8 font-display text-lg font-bold text-slate-900 sm:text-xl">
          {line.slice(4)}
        </h3>,
      );
    } else if (line.startsWith("## ")) {
      flushBullets();
      nodes.push(
        <h2 key={idx} className="mb-4 mt-10 flex items-center gap-3 border-b pb-2 font-display text-xl font-black tracking-tight text-slate-900 sm:text-2xl" style={{ borderColor: `${accent}22` }}>
          <span className="inline-block h-6 w-1 rounded-full" style={{ backgroundColor: accent }} />
          {line.slice(3)}
        </h2>,
      );
    } else if (line.startsWith("# ")) {
      flushBullets();
      nodes.push(
        <h1 key={idx} className="mb-6 font-display text-3xl font-black tracking-tight text-slate-900 sm:text-4xl">
          {line.slice(2)}
        </h1>,
      );
    } else if (line.startsWith("- ") || line.startsWith("* ")) {
      bulletBuffer.push(line.slice(2));
    } else {
      flushBullets();
      nodes.push(
        <p key={idx} className="mb-4 leading-relaxed text-slate-700"
          dangerouslySetInnerHTML={{ __html: safeInline(inline(line)) }}
        />,
      );
    }
  });
  flushBullets();
  return nodes;
}

export default function Legal({ kind = "privacy" }) {
  const { T, brand, content } = useLandingCMS({ panel: "customer" });
  const legal = content?.legal || {};
  const isPrivacy = kind === "privacy";
  const md = isPrivacy ? legal.privacy_policy : legal.terms;
  const title = isPrivacy ? "Privacy Policy" : "Terms & Conditions";
  const subtitle = isPrivacy
    ? "How we collect, use and protect the personal data you share with us."
    : "The rules that govern your use of our fuel delivery service and platform.";
  const Icon = isPrivacy ? ShieldCheck : ScrollText;
  const brandName = brand.name || "FuelExpress";
  const supportEmail = content?.support_contact?.email || content?.contact?.email || "legal@fuelexpress.in";
  const lastUpdated = isPrivacy ? legal.privacy_updated_at : legal.terms_updated_at;

  return (
    <div
      className="relative min-h-screen overflow-hidden"
      style={{ backgroundColor: T.panel_bg }}
      data-testid={`legal-${kind}`}
    >
      {/* Ambient background flourishes — mirrors Login.jsx aesthetic */}
      <div aria-hidden className="pointer-events-none absolute -left-32 -top-32 h-[28rem] w-[28rem] rounded-full opacity-25 blur-3xl" style={{ backgroundColor: T.gradient_from }} />
      <div aria-hidden className="pointer-events-none absolute -right-24 top-96 h-96 w-96 rounded-full opacity-30 blur-3xl" style={{ backgroundColor: T.gradient_to }} />

      {/* Header bar */}
      <header className="relative z-10 border-b border-white/60 bg-white/60 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-4 py-3 sm:px-6 sm:py-4">
          <Link to="/" className="flex items-center gap-2.5">
            {brand.logo_url ? (
              <img
                src={brand.logo_url}
                alt={brandName}
                className="h-10 w-10 rounded-lg bg-white object-contain p-0.5 shadow"
              />
            ) : (
              <div
                className="grid h-10 w-10 place-items-center rounded-lg shadow-lg"
                style={{ background: `linear-gradient(135deg, ${T.gradient_from}, ${T.gradient_to})`, color: T.text_on_gradient }}
              >
                <Fuel size={18} strokeWidth={2.5} />
              </div>
            )}
            <div className="font-display text-lg font-black tracking-tight text-slate-900">
              {brandName}
            </div>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 rounded-full border bg-white px-3 py-1.5 text-xs font-semibold shadow-sm transition-colors"
            style={{ borderColor: `${T.accent}44`, color: T.accent }}
            data-testid="legal-back-home"
          >
            <ArrowLeft size={12} /> <span className="hidden sm:inline">Back to home</span><span className="sm:hidden">Home</span>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section
        className="relative z-10 overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${T.gradient_from}, ${T.gradient_to})`,
          color: T.text_on_gradient,
        }}
      >
        <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6 sm:py-14">
          <div
            className="mb-4 inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-[0.25em] backdrop-blur"
            style={{ borderColor: "rgba(255,255,255,0.25)", background: "rgba(255,255,255,0.10)" }}
          >
            <Icon size={11} /> {isPrivacy ? "Your data · your control" : "Rules of engagement"}
          </div>
          <h1 className="font-display text-4xl font-black leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
            {title}
          </h1>
          <p className="mt-4 max-w-2xl text-base opacity-90 sm:text-lg" style={{ color: "rgba(255,255,255,0.9)" }}>
            {subtitle}
          </p>
          {lastUpdated && (
            <div className="mt-6 inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold" style={{ borderColor: "rgba(255,255,255,0.25)", background: "rgba(0,0,0,0.15)" }}>
              Last updated · {lastUpdated}
            </div>
          )}
        </div>
      </section>

      {/* Content card */}
      <main className="relative z-10 mx-auto max-w-5xl px-4 py-8 sm:px-6 sm:py-12">
        <div
          className="rounded-2xl border bg-white p-6 shadow-xl sm:p-10"
          style={{ borderColor: `${T.accent}22`, boxShadow: `0 25px 50px -12px ${T.accent}22` }}
        >
          {md ? (
            <article className="prose prose-slate max-w-none">
              {renderMarkdown(md, T.accent)}
            </article>
          ) : (
            <div className="py-16 text-center">
              <div
                className="mx-auto grid h-14 w-14 place-items-center rounded-full"
                style={{ backgroundColor: `${T.accent}18`, color: T.accent }}
              >
                <Icon size={26} />
              </div>
              <h2 className="mt-4 font-display text-2xl font-black text-slate-900">{title}</h2>
              <p className="mt-2 text-slate-500">
                Content has not been published yet. Please check back soon.
              </p>
            </div>
          )}

          {/* Trust footer inside the card */}
          <div className="mt-10 border-t pt-6" style={{ borderColor: `${T.accent}22` }}>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Mail size={14} style={{ color: T.accent }} />
                Questions?{" "}
                <a href={`mailto:${supportEmail}`} className="font-semibold underline" style={{ color: T.accent }}>
                  {supportEmail}
                </a>
              </div>
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <Link
                  to="/privacy"
                  className={`rounded-full px-3 py-1 font-semibold transition-colors ${isPrivacy ? "text-white" : "text-slate-600 hover:text-slate-900"}`}
                  style={isPrivacy ? { backgroundColor: T.accent } : {}}
                  data-testid="legal-nav-privacy"
                >
                  Privacy
                </Link>
                <Link
                  to="/terms"
                  className={`rounded-full px-3 py-1 font-semibold transition-colors ${!isPrivacy ? "text-white" : "text-slate-600 hover:text-slate-900"}`}
                  style={!isPrivacy ? { backgroundColor: T.accent } : {}}
                  data-testid="legal-nav-terms"
                >
                  Terms
                </Link>
                <Link to="/" className="rounded-full px-3 py-1 font-semibold text-slate-600 transition-colors hover:text-slate-900">
                  Home
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom-of-page copyright */}
        <div className="mt-6 text-center text-[11px] text-slate-500">
          © {new Date().getFullYear()} {brandName} — All rights reserved
        </div>
      </main>
    </div>
  );
}
